import React, { useState, useCallback, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeHighlight from 'rehype-highlight';
import rehypeKatex from 'rehype-katex';
import styles from '../../styles/ChatApp.module.css';

/**
 * Copy button with transient "Copied!" feedback.
 * Defined outside markdownComponents so the component type is stable across renders.
 */
const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false);
    const [failed, setFailed] = useState(false);

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(text)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(() => {
                setFailed(true);
                setTimeout(() => setFailed(false), 2000);
            });
    }, [text]);

    return (
        <button
            className={styles.codeCopyButton}
            onClick={handleCopy}
            aria-label="Copy code"
        >
            {failed ? 'Failed' : copied ? 'Copied!' : 'Copy'}
        </button>
    );
};

/**
 * Recursively extracts plain text from a React element tree.
 * Needed because rehype-highlight transforms code strings into nested
 * <span> elements for syntax colouring before our `pre` renderer runs,
 * so child.props.children is a tree of objects, not a flat string.
 */
const extractText = (node) => {
    if (typeof node === 'string') return node;
    if (typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(extractText).join('');
    if (node?.props?.children) return extractText(node.props.children);
    return '';
};

/**
 * Module-level constants keep plugin arrays and component map stable
 * so react-markdown never unnecessarily re-mounts the tree.
 */
const REMARK_PLUGINS = [remarkGfm, remarkMath];
const REHYPE_PLUGINS = [rehypeHighlight, rehypeKatex];

/**
 * Override `pre` so we control the entire code-block structure
 * (language label + copy button + syntax-highlighted code).
 * Override `code` to be a pass-through — all block/inline distinction
 * is handled at the `pre` level.
 */
const markdownComponents = {
    pre({ children }) {
        // Extract the inner <code> child to read language and raw text
        const child = React.Children.toArray(children)[0];
        const className = child?.props?.className || '';
        const match = /language-(\w+)/.exec(className);
        // rehype-highlight turns the code into a span tree, so we walk it
        const codeText = extractText(child?.props?.children).replace(/\n$/, '');

        return (
            <div className={styles.codeBlock}>
                <div className={styles.codeHeader}>
                    <span className={styles.codeLanguage}>
                        {match ? match[1] : 'code'}
                    </span>
                    <CopyButton text={codeText} />
                </div>
                <pre>{children}</pre>
            </div>
        );
    },

    // Render code as-is; the pre override handles block wrapping above
    code({ className, children, ...props }) {
        return (
            <code className={className} {...props}>
                {children}
            </code>
        );
    },
};

/**
 * Shared renderer used by both ChatMessage and StreamingMessage.
 * Supports: GFM (tables, strikethrough, task lists), LaTeX math ($ and $$),
 * syntax-highlighted code blocks with language label + copy button,
 * and all standard markdown (headings, lists, blockquotes, links, hr).
 */
const MarkdownRenderer = memo(({ content }) => (
    <div className="markdown-content">
        <ReactMarkdown
            remarkPlugins={REMARK_PLUGINS}
            rehypePlugins={REHYPE_PLUGINS}
            components={markdownComponents}
        >
            {content}
        </ReactMarkdown>
    </div>
));

MarkdownRenderer.displayName = 'MarkdownRenderer';

export default MarkdownRenderer;
